import { useState } from "react";
import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert } from "@/components/ui";
import {
  CatalogTranslatedField,
  CatalogTranslatePanel,
  SectionCard,
} from "@/components/common";
import { resolveLocale, type SupportedLocale } from "@/lib/locales";
import {
  ACTIVITY_DESCRIPTION_MAX_LENGTH,
  ACTIVITY_NAME_MAX_LENGTH,
  type TranslationFormValues,
} from "@/lib/translationResolution";

interface CatalogCopySectionProps {
  nameTranslations: TranslationFormValues;
  onNameTranslationsChange: (values: TranslationFormValues) => void;
  descriptionTranslations: TranslationFormValues;
  onDescriptionTranslationsChange: (values: TranslationFormValues) => void;
  nameError?: string;
  descriptionError?: string;
  modified: boolean;
  /** Show required marker on description (e.g. when activating / already listed). */
  descriptionRequired: boolean;
  onTranslate: () => void;
  isTranslating: boolean;
  translateDisabled: boolean;
}

export function CatalogCopySection({
  nameTranslations,
  onNameTranslationsChange,
  descriptionTranslations,
  onDescriptionTranslationsChange,
  nameError,
  descriptionError,
  modified,
  descriptionRequired,
  onTranslate,
  isTranslating,
  translateDisabled,
}: CatalogCopySectionProps) {
  const { t, i18n } = useTranslation("activities");
  const [copyLanguage, setCopyLanguage] = useState<SupportedLocale>(() =>
    resolveLocale(i18n.language),
  );

  return (
    <SectionCard
      title={t("detailForm.catalogCopyTitle")}
      icon={Languages}
      modified={modified}
      className="mb-6"
    >
      <div className="space-y-6">
        <CatalogTranslatePanel
          hint={t("detailForm.autoTranslateHint")}
          buttonLabel={t("detailForm.autoTranslateGenerate")}
          onTranslate={onTranslate}
          isLoading={isTranslating}
          disabled={translateDisabled || isTranslating}
        />

        <CatalogTranslatedField
          values={nameTranslations}
          onChange={onNameTranslationsChange}
          label={t("detailForm.nameTitle")}
          fieldAriaLabel={(language) =>
            t("detailForm.nameLanguageCardAria", { language })
          }
          error={nameError}
          required
          maxLength={ACTIVITY_NAME_MAX_LENGTH}
          selectedLanguage={copyLanguage}
          onSelectedLanguageChange={setCopyLanguage}
        />

        <CatalogTranslatedField
          values={descriptionTranslations}
          onChange={onDescriptionTranslationsChange}
          label={t("detailForm.descriptionTitle")}
          fieldAriaLabel={(language) =>
            t("detailForm.descriptionLanguageCardAria", { language })
          }
          multiline
          maxLength={ACTIVITY_DESCRIPTION_MAX_LENGTH}
          placeholder={t("detailForm.descriptionPlaceholder")}
          hint={t("detailForm.descriptionHint")}
          error={descriptionError}
          required={descriptionRequired}
          selectedLanguage={copyLanguage}
          onSelectedLanguageChange={setCopyLanguage}
          hideLanguageControls
          banner={
            <Alert
              variant="info"
              className="mb-4"
            >
              {t("detailForm.descriptionCustomerBanner")}
            </Alert>
          }
        />
      </div>
    </SectionCard>
  );
}
